import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getLoginColumns, createLoginColumn, updateLoginColumn, deleteLoginColumn,
  changeColumnSecret, unlockColumn,
  getLoginAccounts, createLoginAccount, updateLoginAccount, deleteLoginAccount,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Lock, LockOpen, Shield, Copy, Settings,
  ChevronDown, ChevronRight, ArrowLeft, KeyRound, ShieldCheck, X, Search,
  ArrowUpAZ, ArrowDownAZ, Clock, ChevronsDownUp, ChevronsUpDown,
} from 'lucide-react';
import { toast } from 'sonner';

// Sensitivity tag palette
const SENS = {
  low:    { label: 'Low',    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  medium: { label: 'Medium', badge: 'bg-amber-100 text-amber-800 border-amber-200' },
  high:   { label: 'High',   badge: 'bg-red-100 text-red-800 border-red-200' },
};

const UNLOCK_LABEL = { none: 'No lock', pin: 'PIN', password: 'Password' };

const emptyAccountForm = {
  account_name: '', column_id: '',
  username: '', password: '', email: '', url: '', phone: '',
  account_pin: '', account_type: '', notes: '',
  security_questions: [],
  two_factor_enabled: false, two_factor_source: '',
};

export default function LoginInfoPage() {
  const navigate = useNavigate();

  const [columns, setColumns] = useState([]);
  const [accountsByCol, setAccountsByCol] = useState({});   // column_id → [accounts]
  const [expandedCols, setExpandedCols] = useState({});     // column_id → bool
  const [expandedAccs, setExpandedAccs] = useState({});     // account_id → bool
  const [loading, setLoading] = useState(true);
  const [showPw, setShowPw] = useState({});                 // account_id → bool

  // Global search — filters ALL visible accounts across every unlocked column.
  const [search, setSearch] = useState('');
  // Per-column sort: column_id → 'az' | 'za' | 'newest' | 'oldest'
  const [sortBy, setSortBy] = useState({});

  // Per-column unlock tokens kept in memory only.
  const [unlockTokens, setUnlockTokens] = useState({});     // column_id → token

  // Dialogs
  const [unlockDialog, setUnlockDialog] = useState({ open: false, column: null, secret: '', reveal: false });
  const [colDialog, setColDialog] = useState({ open: false, editing: null, form: { name: '', sensitivity_level: 'low', unlock_type: 'none', unlock_secret: '' } });
  const [secretDialog, setSecretDialog] = useState({ open: false, column: null, unlock_type: 'none', old_secret: '', new_secret: '', reveal_old: false, reveal_new: false });
  const [accountDialog, setAccountDialog] = useState({ open: false, editing: null, form: { ...emptyAccountForm } });
  const [deleteColDialog, setDeleteColDialog] = useState({ open: false, column: null });

  const tokensArray = () => Object.values(unlockTokens).filter(Boolean);

  const loadColumns = useCallback(async () => {
    try {
      const cols = await getLoginColumns();
      setColumns(cols);
      return cols;
    } catch { toast.error('Failed to load columns'); return []; }
  }, []);

  const loadAccounts = useCallback(async (tokens) => {
    try {
      const accs = await getLoginAccounts(tokens ?? tokensArray());
      const grouped = {};
      for (const a of accs) {
        if (!grouped[a.column_id]) grouped[a.column_id] = [];
        grouped[a.column_id].push(a);
      }
      setAccountsByCol(grouped);
    } catch { toast.error('Failed to load accounts'); }
  }, [unlockTokens]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadColumns();
      // Initial fetch — will only return accounts from unlock_type=none columns
      await loadAccounts([]);
      setLoading(false);
    })();
  }, []);

  // ── Unlock a column ─────────────────────────────────────────────
  const requestUnlock = (col) => {
    if (col.unlock_type === 'none') {
      // Auto-unlock (still calls backend to get a token so accounts fetch is consistent)
      handleUnlockSubmit(col, '');
      return;
    }
    setUnlockDialog({ open: true, column: col, secret: '', reveal: false });
  };

  const handleUnlockSubmit = async (colArg, secretArg) => {
    const col = colArg || unlockDialog.column;
    const secret = secretArg !== undefined ? secretArg : unlockDialog.secret;
    if (!col) return;
    try {
      const { unlock_token } = await unlockColumn(col.id, secret);
      const nextTokens = { ...unlockTokens, [col.id]: unlock_token };
      setUnlockTokens(nextTokens);
      setExpandedCols(p => ({ ...p, [col.id]: true }));
      await loadAccounts(Object.values(nextTokens));
      setUnlockDialog({ open: false, column: null, secret: '', reveal: false });
      toast.success(`${col.name} unlocked`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Incorrect secret');
    }
  };

  const relockColumn = (col) => {
    const next = { ...unlockTokens }; delete next[col.id];
    setUnlockTokens(next);
    setAccountsByCol(prev => { const p = { ...prev }; delete p[col.id]; return p; });
    setExpandedCols(p => ({ ...p, [col.id]: false }));
    toast.success(`${col.name} locked`);
  };

  // ── Column CRUD ────────────────────────────────────────────────
  const openCreateCol = () =>
    setColDialog({ open: true, editing: null, form: { name: '', sensitivity_level: 'low', unlock_type: 'none', unlock_secret: '' } });

  const openEditCol = (col) =>
    setColDialog({ open: true, editing: col, form: { name: col.name, sensitivity_level: col.sensitivity_level, unlock_type: col.unlock_type, unlock_secret: '' } });

  const handleColSave = async () => {
    const { editing, form } = colDialog;
    if (!form.name.trim()) return toast.error('Column name is required');
    try {
      if (editing) {
        // Only name / sensitivity here. Unlock type/secret uses the separate
        // "Change lock" dialog (requires old secret).
        await updateLoginColumn(editing.id, { name: form.name, sensitivity_level: form.sensitivity_level });
        toast.success('Column updated');
      } else {
        if (form.unlock_type !== 'none' && !form.unlock_secret.trim())
          return toast.error(`A ${form.unlock_type} is required for locked columns`);
        await createLoginColumn(form);
        toast.success('Column created');
      }
      setColDialog({ open: false, editing: null, form: { name: '', sensitivity_level: 'low', unlock_type: 'none', unlock_secret: '' } });
      await loadColumns();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save column'); }
  };

  const confirmDeleteCol = (col) => setDeleteColDialog({ open: true, column: col });
  const handleColDelete = async (force) => {
    const col = deleteColDialog.column;
    try {
      await deleteLoginColumn(col.id, force);
      toast.success('Column deleted');
      setDeleteColDialog({ open: false, column: null });
      const next = { ...unlockTokens }; delete next[col.id]; setUnlockTokens(next);
      await loadColumns();
      await loadAccounts(Object.values(next));
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (detail.includes('account')) {
        // Backend requires ?force=true; ask again.
        if (window.confirm(`${detail}\n\nDelete the column AND all its accounts?`)) handleColDelete(true);
      } else { toast.error(detail || 'Failed to delete'); }
    }
  };

  const openSecretDialog = (col) =>
    setSecretDialog({ open: true, column: col, unlock_type: col.unlock_type, old_secret: '', new_secret: '', reveal_old: false, reveal_new: false });

  const handleSecretSave = async () => {
    const s = secretDialog;
    if (s.column.has_secret && !s.old_secret)
      return toast.error('Enter the current secret to change the lock');
    if (s.unlock_type !== 'none' && !s.new_secret)
      return toast.error(`A new ${s.unlock_type} is required`);
    try {
      await changeColumnSecret(s.column.id, {
        unlock_type: s.unlock_type,
        old_secret: s.old_secret,
        new_secret: s.new_secret,
      });
      toast.success('Lock updated');
      setSecretDialog({ open: false, column: null, unlock_type: 'none', old_secret: '', new_secret: '', reveal_old: false, reveal_new: false });
      // Any previously issued token is still valid, but the secret has changed.
      // Clear it to force re-unlock (safer).
      const next = { ...unlockTokens }; delete next[s.column.id]; setUnlockTokens(next);
      setAccountsByCol(prev => { const p = { ...prev }; delete p[s.column.id]; return p; });
      await loadColumns();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update lock'); }
  };

  // ── Account CRUD ───────────────────────────────────────────────
  const openCreateAccount = (col) =>
    setAccountDialog({ open: true, editing: null, form: { ...emptyAccountForm, column_id: col.id } });

  const openEditAccount = (acc) =>
    setAccountDialog({ open: true, editing: acc, form: {
      account_name: acc.account_name || '',
      column_id: acc.column_id || '',
      username: acc.username || '',
      password: acc.password || '',
      email: acc.email || '',
      url: acc.url || '',
      phone: acc.phone || '',
      account_pin: acc.account_pin || '',
      account_type: acc.account_type || '',
      notes: acc.notes || '',
      security_questions: acc.security_questions || [],
      two_factor_enabled: !!acc.two_factor_enabled,
      two_factor_source: acc.two_factor_source || '',
    } });

  const handleAccountSave = async () => {
    const { editing, form } = accountDialog;
    if (!form.account_name.trim()) return toast.error('Account name is required');
    if (!form.column_id) return toast.error('Pick a column');
    try {
      if (editing) await updateLoginAccount(editing.id, form, tokensArray());
      else await createLoginAccount(form, tokensArray());
      toast.success(editing ? 'Account updated' : 'Account created');
      setAccountDialog({ open: false, editing: null, form: { ...emptyAccountForm } });
      await loadAccounts();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save account'); }
  };

  const handleAccountDelete = async (acc) => {
    if (!window.confirm(`Delete "${acc.account_name}"?`)) return;
    try {
      await deleteLoginAccount(acc.id, tokensArray());
      toast.success('Deleted');
      await loadAccounts();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  // ── Search + sort helpers ──────────────────────────────────────
  const matchesSearch = (acc, needle) => {
    if (!needle) return true;
    const n = needle.toLowerCase();
    return (
      (acc.account_name || '').toLowerCase().includes(n) ||
      (acc.username || '').toLowerCase().includes(n) ||
      (acc.email || '').toLowerCase().includes(n) ||
      (acc.account_type || '').toLowerCase().includes(n) ||
      (acc.url || '').toLowerCase().includes(n) ||
      (acc.notes || '').toLowerCase().includes(n)
    );
  };
  const sortAccounts = (arr, mode) => {
    const cmpName = (a, b) => (a.account_name || '').localeCompare(b.account_name || '');
    const cmpDate = (a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    switch (mode) {
      case 'za':      return [...arr].sort((a, b) => cmpName(b, a));
      case 'newest':  return [...arr].sort(cmpDate);
      case 'oldest':  return [...arr].sort((a, b) => -cmpDate(a, b));
      case 'az':
      default:        return [...arr].sort(cmpName);
    }
  };
  // Which columns currently have search matches (for auto-expand behaviour).
  const matchingColIds = search
    ? new Set(Object.entries(accountsByCol)
        .filter(([_, list]) => (list || []).some(a => matchesSearch(a, search)))
        .map(([cid]) => cid))
    : null;

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <Button
        variant="ghost" size="sm"
        className="h-8 text-xs gap-1.5 -ml-2 text-muted-foreground hover:text-foreground"
        onClick={() => navigate('/marketing')}
        data-testid="login-info-back-btn"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Marketing
      </Button>

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2" data-testid="login-info-page-title">
            <KeyRound className="h-5 w-5 text-primary" /> Login Information
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Organize credentials into columns with per-column locks.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const anyOpen = Object.values(expandedCols).some(Boolean);
              if (anyOpen) {
                // Collapse every column (and reset any leftover expanded
                // account detail cards so the next open-all starts clean).
                setExpandedCols({});
                setExpandedAccs({});
              } else {
                // Only open columns the user can actually view (unlocked or
                // unlock_type=none) — no point flipping a lock icon open.
                // Also close every account detail so "Expand all" gives a
                // clean, flat list of account rows (like just opening each
                // column without expanding the accounts inside).
                const next = {};
                for (const c of columns) {
                  const canView = c.unlock_type === 'none' || !!unlockTokens[c.id];
                  if (canView) next[c.id] = true;
                }
                setExpandedCols(next);
                setExpandedAccs({});
              }
            }}
            data-testid="login-info-toggle-all-cols"
          >
            {Object.values(expandedCols).some(Boolean)
              ? <><ChevronsDownUp className="h-4 w-4 mr-1.5" /> Collapse all</>
              : <><ChevronsUpDown className="h-4 w-4 mr-1.5" /> Expand all</>}
          </Button>
          <Button onClick={openCreateCol} data-testid="login-info-add-column-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Column
          </Button>
        </div>
      </div>

      {/* Global search — matches across every unlocked column */}
      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search account name, username, email, type, URL, notes…"
          className="pl-8 pr-8 h-9"
          data-testid="login-info-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
            data-testid="login-info-search-clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {loading ? <div className="text-center py-12 text-muted-foreground text-sm">Loading…</div> : (
        columns.length === 0 ? (
          <div className="text-center py-14 border border-dashed rounded-lg">
            <Shield className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No columns yet. Click <b>New Column</b> to get started.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {columns.map(col => {
              const rawList = accountsByCol[col.id] || [];
              const filtered = search ? rawList.filter(a => matchesSearch(a, search)) : rawList;
              const sorted = sortAccounts(filtered, sortBy[col.id] || 'az');
              // Auto-expand columns that contain a search match; otherwise
              // respect the user's manual toggle.
              const forceOpen = search && matchingColIds && matchingColIds.has(col.id);
              const expanded = forceOpen || !!expandedCols[col.id];
              const hiddenBySearch = search && !forceOpen;
              if (hiddenBySearch) return null;
              return (
                <ColumnRow
                  key={col.id}
                  col={col}
                  expanded={expanded}
                  onToggle={() => setExpandedCols(p => ({ ...p, [col.id]: !p[col.id] }))}
                  unlocked={!!unlockTokens[col.id]}
                  accounts={sorted}
                  totalAccounts={rawList.length}
                  onUnlock={() => requestUnlock(col)}
                  onLock={() => relockColumn(col)}
                  onEditCol={() => openEditCol(col)}
                  onDeleteCol={() => confirmDeleteCol(col)}
                  onChangeLock={() => openSecretDialog(col)}
                  onAddAccount={() => openCreateAccount(col)}
                  expandedAccs={expandedAccs}
                  setExpandedAccs={setExpandedAccs}
                  showPw={showPw}
                  setShowPw={setShowPw}
                  onEditAccount={openEditAccount}
                  onDeleteAccount={handleAccountDelete}
                  onCopy={copyToClipboard}
                  sortMode={sortBy[col.id] || 'az'}
                  onChangeSort={(mode) => setSortBy(p => ({ ...p, [col.id]: mode }))}
                />
              );
            })}
            {search && matchingColIds && matchingColIds.size === 0 && (
              <div className="text-center py-10 border border-dashed rounded-lg text-sm text-muted-foreground">
                No accounts match <b>“{search}”</b>.
              </div>
            )}
          </div>
        )
      )}

      {/* ─── Column Create/Edit Dialog ────────────────────── */}
      <Dialog open={colDialog.open} onOpenChange={o => !o && setColDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{colDialog.editing ? 'Edit Column' : 'New Column'}</DialogTitle>
            <DialogDescription>
              {colDialog.editing
                ? 'Rename or re-tag this column. To change the lock method, use "Change Lock".'
                : 'A column groups related accounts. Pick a sensitivity tag and how it should be unlocked.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5">
              <Label>Column Name</Label>
              <Input value={colDialog.form.name} onChange={e => setColDialog(p => ({ ...p, form: { ...p.form, name: e.target.value } }))}
                placeholder="e.g. Airbnb, Utilities, Marketing Tools" data-testid="col-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Sensitivity Tag</Label>
              <Select value={colDialog.form.sensitivity_level} onValueChange={v => setColDialog(p => ({ ...p, form: { ...p.form, sensitivity_level: v } }))}>
                <SelectTrigger data-testid="col-sens-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!colDialog.editing && (
              <>
                <div className="space-y-1.5">
                  <Label>Unlock Method</Label>
                  <Select value={colDialog.form.unlock_type} onValueChange={v => setColDialog(p => ({ ...p, form: { ...p.form, unlock_type: v } }))}>
                    <SelectTrigger data-testid="col-unlock-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No lock (anyone can view)</SelectItem>
                      <SelectItem value="pin">PIN</SelectItem>
                      <SelectItem value="password">Password</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {colDialog.form.unlock_type !== 'none' && (
                  <div className="space-y-1.5">
                    <Label>{colDialog.form.unlock_type === 'pin' ? 'PIN' : 'Password'}</Label>
                    <Input type="password" value={colDialog.form.unlock_secret}
                      onChange={e => setColDialog(p => ({ ...p, form: { ...p.form, unlock_secret: e.target.value } }))}
                      placeholder={colDialog.form.unlock_type === 'pin' ? '4+ digits' : 'min 4 chars'}
                      data-testid="col-secret-input"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setColDialog(p => ({ ...p, open: false }))}>Cancel</Button>
            <Button onClick={handleColSave} data-testid="col-save-btn">{colDialog.editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Unlock Dialog ─────────────────────────────────── */}
      <Dialog open={unlockDialog.open} onOpenChange={o => !o && setUnlockDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5">
              <Lock className="h-4 w-4" /> Unlock {unlockDialog.column?.name}
            </DialogTitle>
            <DialogDescription>
              Enter the {unlockDialog.column?.unlock_type === 'pin' ? 'PIN' : 'password'} to view accounts.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 relative">
            <Input
              type={unlockDialog.reveal ? 'text' : 'password'}
              placeholder={unlockDialog.column?.unlock_type === 'pin' ? 'PIN' : 'Password'}
              value={unlockDialog.secret}
              onChange={e => setUnlockDialog(p => ({ ...p, secret: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleUnlockSubmit()}
              autoFocus data-testid="unlock-secret-input"
              className="pr-9"
            />
            <button type="button" onClick={() => setUnlockDialog(p => ({ ...p, reveal: !p.reveal }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
              {unlockDialog.reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockDialog(p => ({ ...p, open: false }))}>Cancel</Button>
            <Button onClick={() => handleUnlockSubmit()} data-testid="unlock-verify-btn">
              <LockOpen className="h-4 w-4 mr-1.5" /> Unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Change Lock (Secret) Dialog ────────────────────── */}
      <Dialog open={secretDialog.open} onOpenChange={o => !o && setSecretDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4" /> Change Lock — {secretDialog.column?.name}
            </DialogTitle>
            <DialogDescription>
              {secretDialog.column?.has_secret
                ? 'Enter the current secret, then choose the new lock method.'
                : 'Set an unlock method for this column.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            {secretDialog.column?.has_secret && (
              <div className="space-y-1.5 relative">
                <Label>Current {UNLOCK_LABEL[secretDialog.column.unlock_type]}</Label>
                <Input
                  type={secretDialog.reveal_old ? 'text' : 'password'}
                  value={secretDialog.old_secret}
                  onChange={e => setSecretDialog(p => ({ ...p, old_secret: e.target.value }))}
                  data-testid="secret-old-input" className="pr-9"
                />
                <button type="button" onClick={() => setSecretDialog(p => ({ ...p, reveal_old: !p.reveal_old }))}
                  className="absolute right-2 top-8 text-muted-foreground p-1">
                  {secretDialog.reveal_old ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>New Unlock Method</Label>
              <Select value={secretDialog.unlock_type} onValueChange={v => setSecretDialog(p => ({ ...p, unlock_type: v }))}>
                <SelectTrigger data-testid="secret-new-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No lock</SelectItem>
                  <SelectItem value="pin">PIN</SelectItem>
                  <SelectItem value="password">Password</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {secretDialog.unlock_type !== 'none' && (
              <div className="space-y-1.5 relative">
                <Label>New {UNLOCK_LABEL[secretDialog.unlock_type]}</Label>
                <Input
                  type={secretDialog.reveal_new ? 'text' : 'password'}
                  value={secretDialog.new_secret}
                  onChange={e => setSecretDialog(p => ({ ...p, new_secret: e.target.value }))}
                  data-testid="secret-new-input" className="pr-9"
                />
                <button type="button" onClick={() => setSecretDialog(p => ({ ...p, reveal_new: !p.reveal_new }))}
                  className="absolute right-2 top-8 text-muted-foreground p-1">
                  {secretDialog.reveal_new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSecretDialog(p => ({ ...p, open: false }))}>Cancel</Button>
            <Button onClick={handleSecretSave} data-testid="secret-save-btn">Update Lock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Column Delete Confirm ────────────────────────── */}
      <Dialog open={deleteColDialog.open} onOpenChange={o => !o && setDeleteColDialog({ open: false, column: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete column?</DialogTitle>
            <DialogDescription>
              Delete <b>{deleteColDialog.column?.name}</b>? Accounts inside must be moved or deleted first (the server will refuse otherwise, and offer force-delete).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteColDialog({ open: false, column: null })}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleColDelete(false)} data-testid="col-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Account Create/Edit Dialog ──────────────────── */}
      <AccountFormDialog
        state={accountDialog}
        setState={setAccountDialog}
        columns={columns}
        onSave={handleAccountSave}
      />
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Column row (expandable, horizontal like tenants)
// ────────────────────────────────────────────────────────────────
function ColumnRow({
  col, expanded, onToggle, unlocked, accounts, totalAccounts,
  onUnlock, onLock, onEditCol, onDeleteCol, onChangeLock, onAddAccount,
  expandedAccs, setExpandedAccs, showPw, setShowPw,
  onEditAccount, onDeleteAccount, onCopy,
  sortMode, onChangeSort,
}) {
  const sens = SENS[col.sensitivity_level] || SENS.low;
  const isOpen = col.unlock_type === 'none';
  const canView = isOpen || unlocked;
  const totalCount = totalAccounts != null ? totalAccounts : accounts.length;
  return (
    <div className="border rounded-lg overflow-hidden bg-card" data-testid="login-column-row">
      {/* Header ───────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 p-3 hover:bg-muted/25 transition-colors">
        <button className="flex items-center gap-2 flex-1 text-left min-w-0" onClick={onToggle} data-testid="login-column-toggle">
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
          <span className="font-medium text-sm truncate">{col.name}</span>
          <Badge className={`${sens.badge} border text-[10px] px-1.5 py-0`}>{sens.label}</Badge>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
            {isOpen ? <LockOpen className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />}
            {UNLOCK_LABEL[col.unlock_type]}
          </Badge>
          {canView && <span className="text-xs text-muted-foreground">· {totalCount} account{totalCount === 1 ? '' : 's'}</span>}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {!canView ? (
            <Button variant="outline" size="sm" className="h-7" onClick={onUnlock} data-testid="col-unlock-btn">
              <Lock className="h-3 w-3 mr-1" /> Unlock
            </Button>
          ) : !isOpen && (
            <Button variant="ghost" size="sm" className="h-7 text-muted-foreground" onClick={onLock} data-testid="col-lock-btn">
              <LockOpen className="h-3 w-3 mr-1" /> Lock
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onChangeLock} title="Change lock" data-testid="col-change-lock-btn">
            <ShieldCheck className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEditCol} title="Rename column"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={onDeleteCol} title="Delete column"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Body (expanded) ─────────────────────────────────── */}
      {expanded && (
        <div className="border-t bg-muted/10 p-3">
          {!canView ? (
            <div className="text-center py-6 text-xs text-muted-foreground">
              <Lock className="h-6 w-6 mx-auto mb-1.5 text-muted-foreground/40" />
              This column is locked. Click <b>Unlock</b> to view accounts.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-xs text-muted-foreground">Accounts</span>
                <div className="flex items-center gap-1.5">
                  {accounts.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs px-2 gap-1"
                      onClick={() => {
                        const someOpen = accounts.some(a => expandedAccs[a.id]);
                        setExpandedAccs(p => {
                          const next = { ...p };
                          if (someOpen) {
                            // Close every account in THIS column only.
                            for (const a of accounts) delete next[a.id];
                          } else {
                            for (const a of accounts) next[a.id] = true;
                          }
                          return next;
                        });
                      }}
                      data-testid="col-toggle-all-accounts"
                    >
                      {accounts.some(a => expandedAccs[a.id])
                        ? <><ChevronsDownUp className="h-3 w-3" /><span className="hidden sm:inline">Collapse all</span></>
                        : <><ChevronsUpDown className="h-3 w-3" /><span className="hidden sm:inline">Expand all</span></>}
                    </Button>
                  )}
                  <SortMenu value={sortMode} onChange={onChangeSort} />
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAddAccount} data-testid="col-add-account-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Account
                  </Button>
                </div>
              </div>
              {accounts.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-3 text-center">No accounts yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {accounts.map(acc => (
                    <AccountRow key={acc.id}
                      acc={acc}
                      expanded={!!expandedAccs[acc.id]}
                      onToggle={() => setExpandedAccs(p => ({ ...p, [acc.id]: !p[acc.id] }))}
                      showPw={!!showPw[acc.id]}
                      onTogglePw={() => setShowPw(p => ({ ...p, [acc.id]: !p[acc.id] }))}
                      onEdit={() => onEditAccount(acc)}
                      onDelete={() => onDeleteAccount(acc)}
                      onCopy={onCopy}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// Small dropdown for choosing sort mode inside a column.
function SortMenu({ value, onChange }) {
  const OPTIONS = [
    { key: 'az',     label: 'A → Z',       Icon: ArrowUpAZ },
    { key: 'za',     label: 'Z → A',       Icon: ArrowDownAZ },
    { key: 'newest', label: 'Newest first', Icon: Clock },
    { key: 'oldest', label: 'Oldest first', Icon: Clock },
  ];
  const current = OPTIONS.find(o => o.key === value) || OPTIONS[0];
  const CurrentIcon = current.Icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-xs px-2 gap-1" data-testid="col-sort-btn">
          <CurrentIcon className="h-3 w-3" />
          <span className="hidden sm:inline">{current.label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {OPTIONS.map(o => (
          <DropdownMenuItem key={o.key} onClick={() => onChange(o.key)} className="text-xs gap-2" data-testid={`col-sort-${o.key}`}>
            <o.Icon className="h-3 w-3" />
            {o.label}
            {o.key === value && <span className="ml-auto text-primary">✓</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


// ────────────────────────────────────────────────────────────────
// Account row + expanded detail
// ────────────────────────────────────────────────────────────────
function AccountRow({ acc, expanded, onToggle, showPw, onTogglePw, onEdit, onDelete, onCopy }) {
  return (
    <div className="border rounded-md bg-background" data-testid="login-account-row">
      <div className="flex items-center justify-between gap-2 p-2.5">
        <button className="flex items-center gap-2 flex-1 text-left min-w-0" onClick={onToggle}>
          {expanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="font-medium text-sm truncate">{acc.account_name}</span>
          {acc.account_type && <Badge variant="outline" className="text-[9px] px-1.5 py-0">{acc.account_type}</Badge>}
          {acc.two_factor_enabled && <Badge className="bg-blue-100 text-blue-800 text-[9px] px-1.5 py-0 gap-0.5"><ShieldCheck className="h-2.5 w-2.5" />2FA</Badge>}
        </button>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onEdit} title="Edit"><Pencil className="h-3 w-3" /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={onDelete} title="Delete"><Trash2 className="h-3 w-3" /></Button>
        </div>
      </div>
      {expanded && (
        <div className="border-t bg-muted/20 p-3 text-xs space-y-1.5">
          <DetailRow label="Username"  value={acc.username}    onCopy={onCopy} />
          <DetailRow label="Password"  value={acc.password}    onCopy={onCopy} secret showPw={showPw} onTogglePw={onTogglePw} />
          <DetailRow label="Email"     value={acc.email}       onCopy={onCopy} />
          <DetailRow label="Phone"     value={acc.phone}       onCopy={onCopy} />
          <DetailRow label="Account PIN" value={acc.account_pin} onCopy={onCopy} secret showPw={showPw} onTogglePw={onTogglePw} />
          <DetailRow label="URL"       value={acc.url}         onCopy={onCopy} link />
          <DetailRow label="Notes"     value={acc.notes}       onCopy={onCopy} multiline />
          {acc.two_factor_enabled && <DetailRow label="2FA Source" value={acc.two_factor_source || '—'} onCopy={onCopy} />}
          {(acc.security_questions || []).length > 0 && (
            <div className="pt-1.5 mt-1.5 border-t">
              <div className="text-[10px] font-medium text-muted-foreground mb-1 uppercase tracking-wide">Security Questions</div>
              <div className="space-y-1">
                {(acc.security_questions || []).map((sq, i) => (
                  <div key={i} className="pl-2 border-l-2 border-muted">
                    <div className="text-[11px] text-muted-foreground">{sq.question || <em>(no question)</em>}</div>
                    <div className="font-mono text-xs">{sq.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Only shows the row if there's a value — empty fields stay empty (don't
// collapse the layout or bleed one field into the next).
function DetailRow({ label, value, onCopy, secret, showPw, onTogglePw, link, multiline }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[100px_1fr_auto] gap-2 items-start">
      <span className="text-muted-foreground text-[11px] pt-0.5">{label}</span>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{value}</a>
      ) : multiline ? (
        <span className="whitespace-pre-wrap break-words">{value}</span>
      ) : (
        <span className={secret ? 'font-mono break-all' : 'break-all'}>
          {secret && !showPw ? '••••••••' : value}
        </span>
      )}
      <div className="flex gap-0.5">
        {secret && (
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={onTogglePw}>
            {showPw ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onCopy(value)} title="Copy">
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Account form dialog — dynamic security questions + 2FA
// ────────────────────────────────────────────────────────────────
function AccountFormDialog({ state, setState, columns, onSave }) {
  const [revealPw, setRevealPw] = useState(false);
  const [revealPin, setRevealPin] = useState(false);
  const f = state.form;
  const patch = (fields) => setState(p => ({ ...p, form: { ...p.form, ...fields } }));

  const addQ = () => patch({ security_questions: [...(f.security_questions || []), { question: '', answer: '' }] });
  const updateQ = (i, key, val) => {
    const arr = [...(f.security_questions || [])]; arr[i] = { ...arr[i], [key]: val }; patch({ security_questions: arr });
  };
  const removeQ = (i) => patch({ security_questions: (f.security_questions || []).filter((_, j) => j !== i) });

  return (
    <Dialog open={state.open} onOpenChange={o => !o && setState(p => ({ ...p, open: false }))}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{state.editing ? 'Edit Account' : 'Add Account'}</DialogTitle>
          <DialogDescription>Store login credentials securely.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Account Name *</Label>
              <Input value={f.account_name} onChange={e => patch({ account_name: e.target.value })} data-testid="acc-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Column</Label>
              <Select value={f.column_id} onValueChange={v => patch({ column_id: v })}>
                <SelectTrigger data-testid="acc-col-select"><SelectValue placeholder="Pick a column" /></SelectTrigger>
                <SelectContent>
                  {columns.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({SENS[c.sensitivity_level].label})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Account Type</Label>
            <Input value={f.account_type} onChange={e => patch({ account_type: e.target.value })} placeholder="e.g. Banking, Email, Utility" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Username</Label>
              <Input value={f.username} onChange={e => patch({ username: e.target.value })} />
            </div>
            <div className="space-y-1.5 relative">
              <Label>Password</Label>
              <Input type={revealPw ? 'text' : 'password'} value={f.password} onChange={e => patch({ password: e.target.value })} className="pr-9" />
              <button type="button" onClick={() => setRevealPw(v => !v)} className="absolute right-2 top-8 text-muted-foreground p-1">
                {revealPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email</Label><Input value={f.email} onChange={e => patch({ email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={e => patch({ phone: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>URL</Label><Input value={f.url} onChange={e => patch({ url: e.target.value })} placeholder="https://" /></div>
          <div className="space-y-1.5 relative">
            <Label>Account PIN</Label>
            <Input type={revealPin ? 'text' : 'password'} value={f.account_pin} onChange={e => patch({ account_pin: e.target.value })}
              placeholder="PIN specific to this account (not the column lock)" className="pr-9" />
            <button type="button" onClick={() => setRevealPin(v => !v)} className="absolute right-2 top-8 text-muted-foreground p-1">
              {revealPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {/* 2FA */}
          <div className="border rounded-md p-3 space-y-2 bg-muted/20">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Two-Factor Authentication</Label>
                <p className="text-[11px] text-muted-foreground">Where do the 2FA codes come from?</p>
              </div>
              <Switch
                checked={!!f.two_factor_enabled}
                onCheckedChange={v => patch({ two_factor_enabled: v })}
                data-testid="acc-2fa-toggle"
              />
            </div>
            {f.two_factor_enabled && (
              <Input
                value={f.two_factor_source}
                onChange={e => patch({ two_factor_source: e.target.value })}
                placeholder="e.g. Google Authenticator, SMS to +1 305 555-0100, Authy"
                data-testid="acc-2fa-source-input"
              />
            )}
          </div>

          {/* Security questions */}
          <div className="border rounded-md p-3 space-y-2 bg-muted/20">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Security Questions</Label>
                <p className="text-[11px] text-muted-foreground">Add as many or as few as you need.</p>
              </div>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addQ} data-testid="acc-add-question-btn">
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {(f.security_questions || []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground italic">No questions added.</p>
            ) : (
              <div className="space-y-2">
                {(f.security_questions || []).map((sq, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                    <Input placeholder={`Question ${i + 1}`} value={sq.question || ''} onChange={e => updateQ(i, 'question', e.target.value)} />
                    <Input placeholder="Answer" value={sq.answer || ''} onChange={e => updateQ(i, 'answer', e.target.value)} />
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => removeQ(i)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={f.notes} onChange={e => patch({ notes: e.target.value })} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState(p => ({ ...p, open: false }))}>Cancel</Button>
          <Button onClick={onSave} data-testid="acc-save-btn">{state.editing ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
